import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { refreshAuthSession, logoutSession } from '../api/client';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const TOKEN_STORAGE_KEY = 'customer_token';
const REFRESH_TOKEN_STORAGE_KEY = 'customer_refresh_token';

const decodeTokenUser = (token) => {
    if (!token) return null;
    try {
        const decoded = jwtDecode(token);
        if (decoded?.exp && decoded.exp * 1000 < Date.now()) {
            return null;
        }

        const roles = Array.isArray(decoded.roles) ? decoded.roles : (decoded.role ? [decoded.role] : ['CUSTOMER']);

        return {
            id: decoded.userId || decoded.sub || null,
            email: decoded.sub || '',
            role: roles[0] || 'CUSTOMER',
            roles: roles,
            firstName: decoded.firstName || '',
            lastName: decoded.lastName || '',
            fullName: decoded.name || '',
            profileCompleted: Boolean(decoded.profileCompleted),
            customerId: decoded.customerId || ''
        };
    } catch (_) {
        return null;
    }
};

const normalizeUserPayload = (payloadUser, tokenFallback = null) => {
    const tokenUser = decodeTokenUser(tokenFallback);
    if (!payloadUser) {
        return tokenUser;
    }

    const firstName = payloadUser.firstName || '';
    const lastName = payloadUser.lastName || '';
    const roles = Array.isArray(payloadUser.roles) && payloadUser.roles.length > 0 
        ? payloadUser.roles 
        : (tokenUser?.roles || (payloadUser.role ? [payloadUser.role] : ['CUSTOMER']));

    return {
        id: payloadUser.id ?? tokenUser?.id ?? null,
        email: payloadUser.email ?? tokenUser?.email ?? '',
        role: roles[0] ?? 'CUSTOMER',
        roles: roles,
        firstName,
        lastName,
        fullName: payloadUser.fullName || `${firstName} ${lastName}`.trim(),
        phoneNumber: payloadUser.phoneNumber || '',
        addressLine: payloadUser.addressLine || '',
        city: payloadUser.city || '',
        country: payloadUser.country || '',
        profileImageUrl: payloadUser.profileImageUrl || '',
        profileCompleted: Boolean(payloadUser.profileCompleted),
        customerId: payloadUser.customerId || tokenUser?.customerId || ''
    };
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const restoreSession = async () => {
            // Check if redirected from partner domain (e.g. luxvoo -> luxvono) with token in query params
            if (typeof window !== 'undefined') {
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    const queryToken = urlParams.get('token') || urlParams.get('auth_token');
                    if (queryToken) {
                        localStorage.setItem(TOKEN_STORAGE_KEY, queryToken);
                        const cleanUrl = window.location.pathname + window.location.hash;
                        window.history.replaceState({}, document.title, cleanUrl || '/');
                    }
                } catch (_) {}
            }

            const localToken = localStorage.getItem(TOKEN_STORAGE_KEY);
            const localUser = decodeTokenUser(localToken);
            if (localUser && !cancelled) {
                setUser(localUser);
            }

            try {
                const refreshed = await refreshAuthSession();
                if (cancelled) return;

                const refreshedToken = refreshed?.accessToken || localStorage.getItem(TOKEN_STORAGE_KEY);
                const refreshedUser = normalizeUserPayload(refreshed?.user, refreshedToken);
                if (refreshedUser) {
                    setUser(refreshedUser);
                } else {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
                    setUser(null);
                }
            } catch (_) {
                if (cancelled) return;
                if (!localUser) {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
                    setUser(null);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        restoreSession();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback((authPayloadOrToken) => {
        if (!authPayloadOrToken) {
            return null;
        }

        let payload = null;
        let token = null;

        if (typeof authPayloadOrToken === 'string') {
            token = authPayloadOrToken;
        } else if (typeof authPayloadOrToken === 'object') {
            payload = authPayloadOrToken;
            token = authPayloadOrToken.accessToken || null;
            if (token) {
                localStorage.setItem(TOKEN_STORAGE_KEY, token);
            }
        }

        if (!token) {
            token = localStorage.getItem(TOKEN_STORAGE_KEY);
        }

        const nextUser = normalizeUserPayload(payload?.user, token);
        setUser(nextUser);
        return nextUser;
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutSession(user?.id || null);
        } catch (_) {
            // API call failed, but we still want to clear local state
        } finally {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        }
        setUser(null);
    }, [user?.id]);

    const setProfile = useCallback((profilePayload) => {
        if (!profilePayload) return;
        setUser(prev => ({
            ...(prev || {}),
            ...profilePayload,
            fullName: profilePayload.fullName || `${profilePayload.firstName || ''} ${profilePayload.lastName || ''}`.trim(),
            profileCompleted: Boolean(profilePayload.profileCompleted)
        }));
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, setProfile, isAuthenticated: !!user, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
